import React, { useState, useEffect, useCallback } from "react";
import {
  X, GitBranch, Plus, RefreshCw, Building2, MapPin,
  ArrowRightLeft, Crown, TrendingUp,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";
import { formatIndianCurrency } from "../utils/Const";

const EMPTY_FORM = {
  branchLabel: "", shop_type: "", owner: "", address: "",
  mobile: "", email: "", password: "",
};

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};
const today = () => new Date().toLocaleDateString("en-CA");

export default function BranchesPanel({ onClose }) {
  const { login } = useAuth();
  const [branches, setBranches] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [range, setRange] = useState({ from: monthStart(), to: today() });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, aRes] = await Promise.all([
        api.get("/branches"),
        api.get(`/branches/analytics?from=${range.from}&to=${range.to}`),
      ]);
      setBranches(bRes.data.branches || []);
      setAnalytics(aRes.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load branches");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    const required = ["shop_type", "owner", "address", "mobile", "email", "password"];
    if (required.some(f => !String(form[f]).trim())) {
      toast.error("All fields except label are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/branches", form);
      toast.success("Branch created");
      setShowNew(false);
      setForm(EMPTY_FORM);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create branch");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSwitch = async (branch) => {
    if (branch.isCurrent) return;
    setSwitching(branch._id);
    try {
      const res = await api.post(`/branches/${branch._id}/switch`);
      toast.success(`Switched to ${res.data.branchName}`);
      login(res.data.token); // swaps token + reloads auth context
      onClose();
      window.location.hash = "#/home";
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to switch branch");
      setSwitching(null);
    }
  };

  const maxRevenue = Math.max(1, ...(analytics?.branches || []).map(b => b.revenue));

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center">
              <GitBranch size={15} className="text-cyan-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Branches</h2>
              <p className="text-[10px] text-slate-400">Multi-outlet management & comparison</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNew(v => !v)}
              className="flex items-center gap-1.5 text-xs font-bold bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1.5 rounded-xl transition-colors"
            >
              <Plus size={12} /> New Branch
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* New branch form */}
        {showNew && (
          <div className="border-b border-slate-100 px-5 py-4 space-y-2.5 bg-cyan-50/40">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              New branch — creates a separate login for the outlet
            </p>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Branch label (e.g. Sector 14)" value={form.branchLabel} onChange={e => setForm(f => ({ ...f, branchLabel: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
              <input placeholder="Shop name *" value={form.shop_type} onChange={e => setForm(f => ({ ...f, shop_type: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
              <input placeholder="Manager / owner name *" value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
              <input placeholder="Mobile *" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
              <input placeholder="Email *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
              <input placeholder="Password * (min 6)" type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
            </div>
            <input placeholder="Branch address *" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-3 py-1.5 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
              <button onClick={handleCreate} disabled={submitting}
                className="px-4 py-1.5 text-xs font-bold bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl disabled:opacity-40 transition-colors">
                {submitting ? "Creating…" : "Create Branch"}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Branch list */}
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Your outlets</p>
                {branches.map(b => (
                  <div key={b._id} className={`flex items-center gap-3 rounded-xl border p-3 ${b.isCurrent ? "border-cyan-300 bg-cyan-50/50" : "border-slate-200 bg-white"}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${b.isMain ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                      {b.isMain ? <Crown size={15} /> : <Building2 size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">
                        {b.branchLabel || b.shop_type}
                        {b.isMain && <span className="ml-1.5 text-[9px] font-black text-amber-500 uppercase">Main</span>}
                        {b.isCurrent && <span className="ml-1.5 text-[9px] font-black text-cyan-600 uppercase">· You are here</span>}
                      </p>
                      <p className="flex items-center gap-1 text-[10px] text-slate-400 truncate"><MapPin size={9} /> {b.address}</p>
                    </div>
                    {!b.isCurrent && (
                      <button
                        onClick={() => handleSwitch(b)}
                        disabled={switching === b._id}
                        className="shrink-0 flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
                      >
                        {switching === b._id ? <RefreshCw size={11} className="animate-spin" /> : <ArrowRightLeft size={11} />}
                        Switch
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Comparison */}
              {analytics && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <TrendingUp size={11} /> Branch comparison
                    </p>
                    <div className="flex items-center gap-1.5">
                      <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
                        className="border border-slate-200 rounded-lg px-1.5 py-1 text-[10px] focus:outline-none" />
                      <span className="text-[10px] text-slate-400">→</span>
                      <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
                        className="border border-slate-200 rounded-lg px-1.5 py-1 text-[10px] focus:outline-none" />
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[9px] font-black uppercase tracking-wider text-slate-400">
                      <span className="col-span-5">Branch</span>
                      <span className="col-span-3 text-right">Revenue</span>
                      <span className="col-span-2 text-right">Bills</span>
                      <span className="col-span-2 text-right">Avg bill</span>
                    </div>
                    {analytics.branches.map(b => (
                      <div key={b.branchId} className="px-3 py-2 border-t border-slate-100">
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <span className="col-span-5 text-xs font-bold text-slate-700 truncate">
                            {b.name}{b.isMain ? " ★" : ""}
                          </span>
                          <span className="col-span-3 text-right text-xs font-black text-slate-800">₹{formatIndianCurrency(b.revenue)}</span>
                          <span className="col-span-2 text-right text-xs text-slate-500">{b.bills}</span>
                          <span className="col-span-2 text-right text-xs text-slate-500">₹{formatIndianCurrency(b.avgBill)}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-cyan-500" style={{ width: `${(b.revenue / maxRevenue) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-t border-slate-200">
                      <span className="col-span-5 text-xs font-black text-slate-800">All branches</span>
                      <span className="col-span-3 text-right text-xs font-black text-slate-800">₹{formatIndianCurrency(analytics.totals.revenue)}</span>
                      <span className="col-span-2 text-right text-xs font-bold text-slate-600">{analytics.totals.bills}</span>
                      <span className="col-span-2 text-right text-xs text-slate-400">—</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            Each branch has its own menu, staff, orders & subscription. Switching swaps your session.
          </p>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
